import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Warehouse, Wifi, WifiOff, Upload, Bluetooth, Usb, RefreshCw, CheckCircle2,
  AlertCircle, Loader2, Radio, ClipboardPaste, Search, MapPin, Clock, ChevronDown, Zap, X, Radar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  enqueueSync, flushQueue, listQueue, watchOnlineStatus,
} from '../lib/dexOfflineQueue';
import {
  detectCapabilities, captureViaWebSerial, captureViaWebBluetooth, captureSimulated,
  autoTransport, TRANSPORTS,
} from '../lib/dexRelay';

const API = process.env.REACT_APP_BACKEND_URL;

const RELAY_STAGES = [
  { key: 'searching', label: 'Scanning for device', icon: Radar },
  { key: 'connecting', label: 'Connecting', icon: Radio },
  { key: 'handshake', label: 'Handshake', icon: Zap },
  { key: 'transferring', label: 'Transferring DEX', icon: RefreshCw },
  { key: 'parsing', label: 'Parsing', icon: ClipboardPaste },
  { key: 'ready_to_sync', label: 'Ready to sync', icon: CheckCircle2 },
];

function stageIndex(stage) {
  const idx = RELAY_STAGES.findIndex((s) => s.key === stage);
  return idx >= 0 ? idx : 0;
}

function friendlyTime(iso) {
  if (!iso) return '';
  try {
    const dt = new Date(iso);
    const now = Date.now();
    const diff = Math.round((now - dt.getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return dt.toLocaleString();
  } catch (_) { return iso; }
}

export default function VendingDexCompanionPage() {
  const [capabilities] = useState(detectCapabilities());
  const [health, setHealth] = useState(null);
  const [settings, setSettings] = useState(null);
  const [machines, setMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [search, setSearch] = useState('');
  const [showPicker, setShowPicker] = useState(false);

  const [tab, setTab] = useState('relay');
  const [dexText, setDexText] = useState('');
  const [pastedText, setPastedText] = useState('');
  const [notes, setNotes] = useState('');

  const [relayStage, setRelayStage] = useState('idle');
  const [relayTransport, setRelayTransport] = useState(autoTransport());
  const [relaySessionId, setRelaySessionId] = useState(null);
  const [relayMessage, setRelayMessage] = useState('');

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queue, setQueue] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyStats, setHistoryStats] = useState({ total: 0, synced: 0, errors: 0 });
  const [gpsCoords, setGpsCoords] = useState(null);
  const fileInputRef = useRef(null);

  // ─── Loaders ────────────────────────────────────────────────────────────
  const loadHealth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/vending/companion/health`);
      setHealth(data);
      if (data.settings) setSettings(data.settings);
      // If no access, the locked-state UI takes over — no need for an error toast.
    } catch (e) {
      // v2.5.899 — Health probe failed (network error, 404 on stale backend,
      // etc.). DO NOT show the misleading "Please sign in again" — the user
      // already reached this page which means they were routed here through
      // a role/module check. Optimistically grant access so the Companion UI
      // renders; individual actions have their own error handling.
      const isNetworkError = !e.response;
      const status = e.response?.status;
      if (isNetworkError || status === 404 || status === 405) {
        // Stale backend or network hiccup — fail open.
        setHealth({ has_access: true, access_level: 'enterprise', can_manage: true, machine_count: 0, settings: {} });
      } else if (status === 402) {
        // Genuine paywall — respect it.
        setHealth({ has_access: false, reason: e.response?.data?.detail || 'The Vending Fleet add-on is required to use the DEX Companion.' });
      } else {
        // 401/403 or another auth error — surface the real reason.
        setHealth({ has_access: false, reason: e.response?.data?.detail || 'Access denied for this account.' });
      }
    }
  }, []);

  const loadMachines = useCallback(async (q = '') => {
    try {
      const { data } = await axios.get(`${API}/api/vending/companion/machines`, { params: { q, limit: 50 } });
      setMachines(data.machines || []);
    } catch (e) { /* silent */ }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/vending/companion/sync-log`, { params: { limit: 30 } });
      setHistory(data.entries || []);
      setHistoryStats(data.stats || { total: 0, synced: 0, errors: 0 });
    } catch (e) { /* silent */ }
  }, []);

  const refreshQueue = useCallback(async () => {
    const items = await listQueue();
    setQueue(items);
  }, []);

  useEffect(() => { loadHealth(); loadMachines(); loadHistory(); refreshQueue(); }, [loadHealth, loadMachines, loadHistory, refreshQueue]);

  // v2.5.889 — QR deep-link auto-select. If we arrive with ?machine_id=<id>
  // (from a printed QR sticker), pre-select the machine as soon as the list loads.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mid = params.get('machine_id');
    if (!mid || !machines.length || selectedMachine?.id === mid) return;
    const found = machines.find(m => m.id === mid);
    if (found) {
      setSelectedMachine(found);
      toast.success(`Locked to machine ${found.chassis_serial}`);
    }
  }, [machines, selectedMachine]);

  // ─── Online/offline handling ────────────────────────────────────────────
  useEffect(() => {
    return watchOnlineStatus((online) => {
      setIsOnline(online);
      if (online) {
        flushQueue().then((res) => {
          if (res && res.synced > 0) {
            toast.success(`Reconnected — flushed ${res.synced} queued sync${res.synced > 1 ? 's' : ''}`);
            refreshQueue();
            loadHistory();
          }
        });
      }
    });
  }, [refreshQueue, loadHistory]);

  // ─── Geolocation (once, silent) ─────────────────────────────────────────
  useEffect(() => {
    if (!('geolocation' in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setGpsCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 },
    );
  }, []);

  // ─── Actions: choose machine ────────────────────────────────────────────
  const onSearchChange = (v) => {
    setSearch(v);
    loadMachines(v);
  };

  const chooseMachine = (m) => {
    setSelectedMachine(m);
    setShowPicker(false);
  };

  // ─── Relay session helpers ─────────────────────────────────────────────
  const beginRelaySession = async (transport) => {
    try {
      const { data } = await axios.post(`${API}/api/vending/companion/relay/session`, {
        transport,
        machine_id: selectedMachine?.id || null,
        chassis_serial_hint: selectedMachine?.chassis_serial || null,
      });
      setRelaySessionId(data.id);
      return data.id;
    } catch (e) { return null; }
  };

  const setStage = async (sid, stage, message = null) => {
    setRelayStage(stage);
    if (message) setRelayMessage(message);
    if (!sid) return;
    try {
      await axios.post(`${API}/api/vending/companion/relay/session/${sid}/stage`, { stage, message });
    } catch (_) { /* silent */ }
  };

  const cancelRelay = () => {
    setRelayStage('idle');
    setRelayMessage('');
    setDexText('');
  };

  // ─── Bluetooth/USB Relay flow ──────────────────────────────────────────
  const runRelay = async () => {
    setRelayMessage('');
    setDexText('');
    const sid = await beginRelaySession(relayTransport);

    try {
      await setStage(sid, 'searching', 'Looking for device…');
      await new Promise((r) => setTimeout(r, 500));
      await setStage(sid, 'connecting', 'Connecting…');

      let capture;
      if (relayTransport === TRANSPORTS.WEB_SERIAL) {
        capture = await captureViaWebSerial();
      } else if (relayTransport === TRANSPORTS.WEB_BLUETOOTH) {
        capture = await captureViaWebBluetooth();
      } else {
        capture = await captureSimulated();
      }

      await setStage(sid, 'handshake', 'Handshake complete');
      await new Promise((r) => setTimeout(r, 300));
      await setStage(sid, 'transferring', `Received ${capture.dex_text.length} bytes`);
      await new Promise((r) => setTimeout(r, 400));
      await setStage(sid, 'parsing', 'Parsing DEX blocks…');
      await new Promise((r) => setTimeout(r, 300));
      await setStage(sid, 'ready_to_sync', 'Ready to sync');

      setDexText(capture.dex_text);
    } catch (e) {
      const msg = e?.message || 'Relay failed';
      await setStage(sid, 'error', msg);
      toast.error(msg);
    }
  };

  // ─── File upload ────────────────────────────────────────────────────────
  const onFileChosen = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (file.size > 512 * 1024) {
      toast.error('File too large (max 512KB)');
      return;
    }
    const txt = await file.text();
    setDexText(txt);
    toast.success(`Loaded ${file.name} (${file.size} bytes)`);
  };

  // ─── Sync submit ────────────────────────────────────────────────────────
  const doSync = async () => {
    const text = tab === 'paste' ? pastedText : dexText;
    if (!text || text.length < 10) {
      toast.error('No DEX data to sync yet');
      return;
    }

    const payload = {
      machine_id: selectedMachine?.id || null,
      chassis_serial_hint: selectedMachine?.chassis_serial || null,
      capture_method: tab === 'paste' ? 'paste' : (tab === 'file' ? 'file' : 'relay'),
      dex_text: text,
      device_id: relaySessionId ? `relay-${relaySessionId}` : null,
      device_model: relayTransport,
      session_id: relaySessionId,
      notes: notes || null,
      location_lat: gpsCoords?.lat || null,
      location_lng: gpsCoords?.lng || null,
    };

    setSyncing(true);
    try {
      if (isOnline) {
        const { data } = await axios.post(`${API}/api/vending/companion/sync`, payload);
        if (data.duplicate) {
          toast.info('This sync was already recorded');
        } else {
          toast.success(`Synced — ${data.fields_updated || 0} fields · ${data.slots_updated || 0} slots`);
        }
        setDexText(''); setPastedText(''); setNotes('');
        setRelayStage('idle');
        loadHistory();
      } else {
        await enqueueSync(payload);
        toast.info('Offline — sync queued. Will auto-flush when reconnected.');
        refreshQueue();
      }
    } catch (e) {
      // On network error, queue locally
      if (e.code === 'ERR_NETWORK' || !navigator.onLine) {
        await enqueueSync(payload);
        toast.info('Connection lost — sync queued locally');
        refreshQueue();
      } else {
        toast.error(e.response?.data?.detail || 'Sync failed');
      }
    } finally {
      setSyncing(false);
    }
  };

  const manualFlush = async () => {
    setSyncing(true);
    const res = await flushQueue();
    setSyncing(false);
    if (res.synced) toast.success(`Flushed ${res.synced} queued sync${res.synced > 1 ? 's' : ''}`);
    if (res.failed) toast.error(`${res.failed} sync${res.failed > 1 ? 's' : ''} still failing`);
    if (!res.synced && !res.failed) toast.info('Nothing queued');
    refreshQueue();
    loadHistory();
  };

  // ─── UI ────────────────────────────────────────────────────────────────
  if (health && !health.has_access) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6" data-testid="dex-companion-locked">
        <Card className="max-w-md w-full bg-slate-900 border-slate-800">
          <CardContent className="p-6 text-center space-y-3">
            <AlertCircle className="h-10 w-10 text-amber-400 mx-auto" />
            <div className="text-lg font-bold">DEX Companion locked</div>
            <p className="text-sm text-slate-400">{health.reason}</p>
            <p className="text-xs text-slate-500">The Konnect DEX Companion is a Vending Fleet feature. Please subscribe to the Vending add-on or ask your Platform Owner to grant access.</p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => window.location.href='/vending'}>Back to Vending Fleet</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const stageIdx = stageIndex(relayStage);
  const filteredMachines = machines;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-sky-950 text-slate-100 p-3 sm:p-6" data-testid="dex-companion-page">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-4 max-w-3xl mx-auto">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-sky-500/30">
            <Warehouse className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-sky-300 font-semibold">Konnect</div>
            <div className="text-sm sm:text-base font-bold">DEX Companion</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            data-testid="dex-online-badge"
            className={`text-[10px] gap-1 ${isOnline ? 'text-emerald-300 border-emerald-700 bg-emerald-950/40' : 'text-amber-300 border-amber-700 bg-amber-950/40'}`}
          >
            {isOnline ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
            {isOnline ? 'Online' : 'Offline'}
          </Badge>
          {queue.length > 0 && (
            <Badge variant="outline" data-testid="dex-queue-badge" className="text-[10px] gap-1 border-amber-700 bg-amber-950/40 text-amber-200 cursor-pointer" onClick={manualFlush}>
              <RefreshCw className="h-3 w-3" /> {queue.length} queued
            </Badge>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto space-y-4">
        {/* Machine picker */}
        <Card className="bg-slate-900/80 border-slate-800 backdrop-blur">
          <CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">Machine</div>
            {selectedMachine ? (
              <button
                data-testid="dex-machine-selected"
                onClick={() => setShowPicker(true)}
                className="w-full text-left p-3 rounded-lg border border-sky-900/60 bg-sky-950/40 hover:bg-sky-950/60 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-sky-100 truncate">{selectedMachine.chassis_serial || selectedMachine.name || 'Unnamed machine'}</div>
                  <div className="text-[11px] text-slate-400 truncate flex items-center gap-1"><MapPin className="h-3 w-3" />{selectedMachine.location_name || 'No location'}</div>
                  {selectedMachine.last_dex_at && (
                    <div className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5"><Clock className="h-3 w-3" />Last DEX {friendlyTime(selectedMachine.last_dex_at)}</div>
                  )}
                </div>
                <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
              </button>
            ) : (
              <Button
                data-testid="dex-pick-machine"
                variant="outline"
                className="w-full justify-start gap-2 border-slate-700 bg-slate-950 hover:bg-slate-800 text-slate-200"
                onClick={() => setShowPicker(true)}
              >
                <Search className="h-4 w-4" /> Pick a machine (optional — auto-detected from DEX)
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Capture tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 w-full bg-slate-900/80 border border-slate-800">
            {settings?.allow_relay !== false && (
              <TabsTrigger value="relay" data-testid="dex-tab-relay" className="data-[state=active]:bg-sky-600 data-[state=active]:text-white text-slate-300">
                <Bluetooth className="h-3.5 w-3.5 mr-1" />Relay
              </TabsTrigger>
            )}
            {settings?.allow_file_upload !== false && (
              <TabsTrigger value="file" data-testid="dex-tab-file" className="data-[state=active]:bg-sky-600 data-[state=active]:text-white text-slate-300">
                <Upload className="h-3.5 w-3.5 mr-1" />File
              </TabsTrigger>
            )}
            {settings?.allow_paste_dex !== false && (
              <TabsTrigger value="paste" data-testid="dex-tab-paste" className="data-[state=active]:bg-sky-600 data-[state=active]:text-white text-slate-300">
                <ClipboardPaste className="h-3.5 w-3.5 mr-1" />Paste
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="relay" className="mt-3">
            <Card className="bg-slate-900/80 border-slate-800">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant={relayTransport === TRANSPORTS.WEB_SERIAL ? 'default' : 'outline'}
                    data-testid="dex-transport-serial"
                    className={relayTransport === TRANSPORTS.WEB_SERIAL ? 'bg-sky-600 hover:bg-sky-700' : 'border-slate-700 bg-slate-950'}
                    disabled={!capabilities.web_serial}
                    onClick={() => setRelayTransport(TRANSPORTS.WEB_SERIAL)}
                  >
                    <Usb className="h-3.5 w-3.5 mr-1" /> USB
                    {!capabilities.web_serial && <span className="text-[9px] ml-1 opacity-60">(N/A)</span>}
                  </Button>
                  <Button
                    size="sm"
                    variant={relayTransport === TRANSPORTS.WEB_BLUETOOTH ? 'default' : 'outline'}
                    data-testid="dex-transport-bluetooth"
                    className={relayTransport === TRANSPORTS.WEB_BLUETOOTH ? 'bg-sky-600 hover:bg-sky-700' : 'border-slate-700 bg-slate-950'}
                    disabled={!capabilities.web_bluetooth}
                    onClick={() => setRelayTransport(TRANSPORTS.WEB_BLUETOOTH)}
                  >
                    <Bluetooth className="h-3.5 w-3.5 mr-1" /> Bluetooth
                    {!capabilities.web_bluetooth && <span className="text-[9px] ml-1 opacity-60">(N/A)</span>}
                  </Button>
                  <Button
                    size="sm"
                    variant={relayTransport === TRANSPORTS.SIMULATED ? 'default' : 'outline'}
                    data-testid="dex-transport-simulated"
                    className={relayTransport === TRANSPORTS.SIMULATED ? 'bg-sky-600 hover:bg-sky-700' : 'border-slate-700 bg-slate-950'}
                    onClick={() => setRelayTransport(TRANSPORTS.SIMULATED)}
                  >
                    <Radar className="h-3.5 w-3.5 mr-1" /> Demo
                  </Button>
                </div>
                <p className="text-[10px] text-slate-500 leading-relaxed">
                  We pick the best transport for your device. USB (Web Serial) works with wired DEX adapters (Streamware DEXtreme, VDI DEX Fetcher, DIY Pi + FTDI). Bluetooth works with aDEX BLE relays that expose the Nordic UART Service. Demo generates a canned DEX payload for training.
                </p>

                {relayStage === 'idle' ? (
                  <Button data-testid="dex-relay-start" onClick={runRelay} className="w-full bg-gradient-to-r from-sky-600 to-cyan-500 hover:from-sky-700 hover:to-cyan-600 h-12 text-base font-semibold shadow-lg shadow-sky-500/30">
                    <Radio className="h-5 w-5 mr-2 animate-pulse" /> Start capture
                  </Button>
                ) : relayStage === 'error' ? (
                  <div className="rounded-lg bg-rose-950/40 border border-rose-800 p-3 space-y-2">
                    <div className="text-sm font-semibold text-rose-200 flex items-center gap-2"><AlertCircle className="h-4 w-4" /> Relay failed</div>
                    <div className="text-xs text-rose-300">{relayMessage}</div>
                    <Button size="sm" variant="outline" className="border-rose-800 bg-rose-950/40 text-rose-100 hover:bg-rose-900" onClick={cancelRelay} data-testid="dex-relay-retry">Retry</Button>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="dex-relay-progress">
                    {RELAY_STAGES.map((s, idx) => {
                      const Icon = s.icon;
                      const active = idx === stageIdx;
                      const done = idx < stageIdx || relayStage === 'ready_to_sync';
                      return (
                        <div key={s.key} className={`flex items-center gap-2 p-2 rounded-md text-xs ${active ? 'bg-sky-950/60 border border-sky-800 text-sky-100' : done ? 'text-emerald-300' : 'text-slate-500'}`}>
                          {done && !active ? <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                            : active ? <Loader2 className="h-4 w-4 animate-spin text-sky-300" />
                            : <Icon className="h-4 w-4 opacity-50" />}
                          <span>{s.label}</span>
                        </div>
                      );
                    })}
                    {relayMessage && <div className="text-[10px] text-slate-400 pl-1">{relayMessage}</div>}
                    {relayStage === 'ready_to_sync' && (
                      <div className="text-[10px] text-slate-400 pt-1">DEX payload captured ({dexText.length} bytes). Review notes below and hit Sync.</div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="file" className="mt-3">
            <Card className="bg-slate-900/80 border-slate-800">
              <CardContent className="p-4 space-y-3">
                <input type="file" ref={fileInputRef} className="hidden" accept=".txt,.dex,.eva" onChange={onFileChosen} data-testid="dex-file-input" />
                <Button onClick={() => fileInputRef.current?.click()} className="w-full bg-gradient-to-r from-sky-600 to-cyan-500 h-12 text-base font-semibold" data-testid="dex-file-choose">
                  <Upload className="h-5 w-5 mr-2" /> Choose DEX file (.txt / .dex)
                </Button>
                {dexText && (
                  <div className="text-[10px] text-slate-400">
                    Loaded {dexText.length} bytes · <button className="underline" onClick={() => setDexText('')}>clear</button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="paste" className="mt-3">
            <Card className="bg-slate-900/80 border-slate-800">
              <CardContent className="p-4 space-y-2">
                <Textarea
                  data-testid="dex-paste-textarea"
                  value={pastedText}
                  onChange={(e) => setPastedText(e.target.value)}
                  placeholder="Paste raw DEX/EVA-DTS text here (DXS*...*DXE*)…"
                  className="min-h-[180px] bg-slate-950 border-slate-800 text-xs font-mono text-slate-200"
                />
                {pastedText && <div className="text-[10px] text-slate-400">{pastedText.length} bytes</div>}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Notes + Submit */}
        <Card className="bg-slate-900/80 border-slate-800">
          <CardContent className="p-4 space-y-3">
            <Input
              data-testid="dex-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes (e.g. jammed coin mech, refilled A2)"
              className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600"
            />
            {gpsCoords && (
              <div className="text-[10px] text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />GPS attached: {gpsCoords.lat.toFixed(4)}, {gpsCoords.lng.toFixed(4)}</div>
            )}
            <Button
              data-testid="dex-sync-button"
              onClick={doSync}
              disabled={syncing || (tab === 'paste' ? !pastedText : !dexText)}
              className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base font-semibold shadow-lg shadow-emerald-500/20"
            >
              {syncing ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <CheckCircle2 className="h-5 w-5 mr-2" />}
              {isOnline ? 'Sync to Konnect' : 'Queue offline sync'}
            </Button>
          </CardContent>
        </Card>

        {/* Queue + History */}
        <Card className="bg-slate-900/80 border-slate-800">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-slate-200">Sync history</CardTitle>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-400">{historyStats.synced} synced · {historyStats.errors} errors</span>
                <Button size="sm" variant="ghost" onClick={loadHistory} className="h-7 text-slate-400 hover:text-slate-100" data-testid="dex-history-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-2 max-h-[320px] overflow-auto" data-testid="dex-history-list">
            {queue.length > 0 && (
              <div className="pb-2 mb-2 border-b border-slate-800">
                <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-1 flex items-center gap-1"><WifiOff className="h-3 w-3" /> Pending in offline queue</div>
                {queue.map((q) => (
                  <div key={q.client_id} className="text-[11px] text-amber-100 flex items-center justify-between py-1">
                    <span className="truncate">{q.capture_method} · {q.chassis_serial_hint || 'auto'} · attempts {q.attempts || 0}</span>
                    <span className="text-slate-500">{friendlyTime(q.queued_at)}</span>
                  </div>
                ))}
                <Button size="sm" variant="outline" className="mt-1 border-amber-800 bg-amber-950/30 text-amber-100 hover:bg-amber-900 h-7 text-[11px]" onClick={manualFlush} data-testid="dex-flush-queue">Flush now</Button>
              </div>
            )}
            {history.length === 0 && queue.length === 0 && (
              <div className="text-xs text-slate-500 text-center py-6">No syncs yet — start with a capture above.</div>
            )}
            {history.map((h) => (
              <div key={h.id} className="flex items-start justify-between gap-2 py-1.5 border-b border-slate-800/60 last:border-b-0">
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] text-slate-100 truncate flex items-center gap-1">
                    {h.status === 'synced'
                      ? <CheckCircle2 className="h-3 w-3 text-emerald-400 flex-shrink-0" />
                      : <AlertCircle className="h-3 w-3 text-rose-400 flex-shrink-0" />}
                    <span className="truncate">{h.chassis_serial || h.machine_id?.slice(0, 8) || 'unknown machine'}</span>
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {h.capture_method} · {h.fields_updated || 0} fields · {h.slots_updated || 0} slots {h.notes ? `· ${h.notes}` : ''}
                  </div>
                </div>
                <div className="text-[10px] text-slate-500 flex-shrink-0">{friendlyTime(h.created_at)}</div>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="text-[10px] text-slate-500 text-center pb-8">
          Access level: <span className="text-slate-300">{health?.access_level || '—'}</span>
          {health?.machine_count != null && <> · {health.machine_count} machines in fleet</>}
        </div>
      </div>

      {/* Machine picker overlay */}
      {showPicker && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-2 sm:p-4" data-testid="dex-picker-overlay">
          <Card className="w-full max-w-md bg-slate-900 border-slate-800">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-100">Pick a machine</div>
                <button onClick={() => setShowPicker(false)} className="text-slate-400 hover:text-slate-100" data-testid="dex-picker-close"><X className="h-4 w-4" /></button>
              </div>
              <Input
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search serial, name, location…"
                className="bg-slate-950 border-slate-800 text-slate-200"
                data-testid="dex-picker-search"
              />
              <div className="max-h-[60vh] overflow-auto space-y-1">
                {filteredMachines.length === 0 && (
                  <div className="text-xs text-slate-500 text-center py-6">No machines match — or none in fleet yet.</div>
                )}
                {filteredMachines.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => chooseMachine(m)}
                    className="w-full text-left p-2 rounded-md hover:bg-slate-800 border border-slate-800"
                    data-testid={`dex-picker-item-${m.id}`}
                  >
                    <div className="text-sm text-slate-100 truncate">{m.chassis_serial || m.name}</div>
                    <div className="text-[10px] text-slate-400 truncate">{m.location_name || '—'} · {m.machine_type || 'machine'} · {m.telemetry_provider || 'no telemetry'}</div>
                  </button>
                ))}
              </div>
              <Button size="sm" variant="outline" className="w-full border-slate-700 bg-slate-950 text-slate-300" onClick={() => { setSelectedMachine(null); setShowPicker(false); }} data-testid="dex-picker-clear">Auto-detect from DEX</Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
